<script setup lang="ts">
import { ArrowRight, Check } from 'lucide-vue-next'

defineProps<{
  id: string
  tag: string
  title: string
  desc: string
  bullets: string[]
  cta: string
  image: string
  reverse?: boolean
  imageClass?: string
}>()

const emit = defineEmits<{ (e: 'action'): void }>()
</script>

<template>
  <section :id="id" class="scroll-mt-20 py-10 sm:py-16">
    <div class="mx-auto grid max-w-6xl items-center gap-9 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
      <div v-reveal="reverse ? 'right' : 'left'" :class="reverse ? 'lg:order-2' : ''">
        <div
          class="inline-flex items-center gap-2 rounded-full border border-primary-100 bg-primary-50/80 px-3 py-1 text-[11px] font-medium text-primary sm:text-xs dark:border-primary-800/60 dark:bg-primary-900/30"
        >
          <span class="h-1.5 w-1.5 rounded-full bg-primary"></span>
          {{ tag }}
        </div>
        <div class="mt-4 text-2xl font-black leading-tight tracking-tight sm:text-3xl lg:text-[38px]">
          {{ title }}
        </div>
        <div class="mt-3 text-sm leading-relaxed text-gray-500 sm:text-base dark:text-gray-400">{{ desc }}</div>
        <div class="mt-6 space-y-2.5">
          <div
            v-for="bullet in bullets"
            :key="bullet"
            class="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
          >
            <Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>{{ bullet }}</span>
          </div>
        </div>
        <div class="mt-7">
          <a-button type="primary" class="!h-11 !rounded-full !px-6" @click="emit('action')">
            <div class="inline-flex items-center gap-1.5">
              {{ cta }}
              <ArrowRight class="h-4 w-4" aria-hidden="true" />
            </div>
          </a-button>
        </div>
      </div>

      <div :class="reverse ? 'lg:order-1' : ''" class="relative">
        <div class="absolute inset-8 rounded-[36px] bg-gradient-to-br from-primary/20 via-accent/15 to-primary/10 blur-2xl"></div>
        <div
          v-reveal="reverse ? 'left' : 'right'"
          class="group relative overflow-hidden rounded-[24px] border border-gray-100 shadow-[0_20px_60px_rgba(15,23,42,0.10)] sm:rounded-[32px] dark:border-gray-800"
          :class="imageClass"
        >
          <img
            :src="image"
            alt=""
            loading="lazy"
            class="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] motion-reduce:transition-none"
          />
          <slot />
        </div>
      </div>
    </div>
  </section>
</template>
